import type { SignalDocument } from "../../../services/signalDocuments/signalDocumentsEndpoints.interface";

export type GroupedAccount = {
  entityId: string;
  accountName: string;
  latestStatus: string | null;
  latestDate: string | null;
  latestSummary: string | null;
  documents: SignalDocument[];
};

export type HealthStatusKey = "healthy" | "at_risk" | "critical";

export type HealthStatusStyles = Record<string, { badge: string; dot: string }>;

export const HEALTH_STATUS_STYLES: HealthStatusStyles = {
  healthy: {
    badge: "border-emerald-200 bg-emerald-50 text-emerald-700",
    dot: "bg-emerald-500",
  },
  at_risk: {
    badge: "border-amber-200 bg-amber-50 text-amber-700",
    dot: "bg-amber-500",
  },
  critical: {
    badge: "border-rose-200 bg-rose-50 text-rose-700",
    dot: "bg-rose-500",
  },
};

export const COLUMN_CONFIG: { key: HealthStatusKey; label: string; dotColor: string }[] = [
  { key: "healthy", label: "Healthy", dotColor: "bg-emerald-500" },
  { key: "at_risk", label: "At Risk", dotColor: "bg-amber-500" },
  { key: "critical", label: "Critical", dotColor: "bg-rose-500" },
];

export const PAGE_SIZE = 20;

/** Parse structured_result which may be a JSON string or object */
export const parseStructured = (content: SignalDocument["structured_result"]) => {
  if (!content) return null;
  if (typeof content === "string") {
    try {
      const parsed = JSON.parse(content);
      return parsed && typeof parsed === "object" ? (parsed as Record<string, any>) : null;
    } catch {
      return null;
    }
  }
  return typeof content === "object" ? content : null;
};

/** Recursively search for health_status in a nested object (e.g. wrapped in { result: ... } or { output: ... }) */
const findHealthStatus = (obj: Record<string, any>, depth = 0): Record<string, any> | null => {
  if (depth > 3) return null;
  if (typeof obj?.health_status === "string") return obj;
  // Check common wrapper keys
  for (const key of ["result", "output", "data", "response", "structured_output"]) {
    const nested = obj?.[key];
    if (nested && typeof nested === "object") {
      const found = findHealthStatus(nested, depth + 1);
      if (found) return found;
    }
    if (typeof nested === "string") {
      try {
        const parsed = JSON.parse(nested);
        if (parsed && typeof parsed === "object") {
          const found = findHealthStatus(parsed, depth + 1);
          if (found) return found;
        }
      } catch { /* ignore */ }
    }
  }
  return null;
};

/** Extract health_status from structured_result, content_structured, or JSON code blocks in markdown. */
export const extractHealthInfo = (doc: SignalDocument): Record<string, any> | null => {
  // Try structured_result first, then content_structured
  for (const field of [doc.structured_result, doc.content_structured]) {
    const structured = parseStructured(field);
    if (!structured) continue;
    if (structured.health_status) return structured;
    // Check nested wrappers
    const nested = findHealthStatus(structured);
    if (nested) return { ...structured, ...nested };
  }

  // Fallback: parse JSON code blocks from markdown
  const md = doc.content_markdown;
  if (!md) return null;
  const jsonBlockRegex = /```json\s*\n([\s\S]*?)```/g;
  let match: RegExpExecArray | null;
  while ((match = jsonBlockRegex.exec(md)) !== null) {
    try {
      const parsed = JSON.parse(match[1]);
      if (parsed?.health_status) return parsed;
      const nested = findHealthStatus(parsed);
      if (nested) return { ...parsed, ...nested };
    } catch {
      // ignore malformed blocks
    }
  }

  // Last resort: scan markdown for inline JSON containing health_status
  const inlineJsonRegex = /\{[^{}]*"health_status"\s*:\s*"(healthy|at_risk|critical)"[^{}]*\}/g;
  const inlineMatch = inlineJsonRegex.exec(md);
  if (inlineMatch) {
    try {
      return JSON.parse(inlineMatch[0]);
    } catch { /* ignore */ }
  }

  return parseStructured(doc.structured_result ?? doc.content_structured);
};
